<template>
  <v-container fluid class="align-start px-md-8 pt-md-6" style="background-color: #ffffff;">
    <v-container v-if="loading">
      <v-row>
        <v-col>
          <v-progress-circular indeterminate />
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else-if="training" fluid class="pa-0">
      <v-overlay v-if="isSaving">
        <v-progress-circular indeterminate size="64" />
      </v-overlay>

      <!-- Header Row -->
      <v-row class="mb-2">
        <!-- Left Column: Info -->
        <v-col cols="12" md="8" class="text-start pr-md-6">
          
          <!-- Mobile Actions -->
          <v-row v-if="$vuetify.breakpoint.smAndDown" no-gutters justify="end" class="mb-4">
            <v-col v-if="$store.getters.isLocaleEnabled" cols="auto">
              <v-btn depressed min-width="40" color="primaryDark" class="white--text" @click="selectedLocale = selectedLocale === 'en' ? 'ar' : 'en'">
                {{ selectedLocale === "ar" ? "En" : "ع" }}
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <CourseSaveAndSubmit :training="training" :isSaving.sync="isSaving" :isEditing.sync="isEditing" @onCancel="resetTraining" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
            </v-col>
            <v-col cols="auto">
              <CourseApproveOrReject :training="training" :isEditing="isEditing" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
            </v-col>
          </v-row>

          <CourseTitle :training.sync="training" :isEditing="isEditing" :selectedLocale="selectedLocale" class="mb-4" />

          <CourseCategory :training.sync="training" :isEditing="isEditing" class="mb-6" />

          <div class="d-flex flex-wrap align-center text-start">
            <div class="mr-8 mb-2">
              <CourseDuration :training.sync="training" :isEditing="isEditing" />
            </div>
            <div class="mr-8 mb-2">
              <CourseValidity :training.sync="training" :isEditing="isEditing" />
            </div>
            <div class="mb-2">
              <CoursePrice :training.sync="training" :isEditing="isEditing" />
            </div>
          </div>
        </v-col>

        <!-- Right Column: Actions & Image -->
        <v-col cols="12" md="4" class="d-flex flex-column align-end">
          <!-- Desktop Actions -->
          <v-row v-if="$vuetify.breakpoint.mdAndUp" no-gutters justify="end" class="mb-4 w-100" style="width: 100%;">
            <v-spacer></v-spacer>
            <v-col v-if="$store.getters.isLocaleEnabled" cols="auto" class="pr-2">
              <v-btn depressed color="primaryDark" class="white--text" @click="selectedLocale = selectedLocale === 'en' ? 'ar' : 'en'">
                {{ selectedLocale === "ar" ? "En" : "ع" }}
              </v-btn>
            </v-col>
            <v-col cols="auto" class="pr-2">
              <CourseSaveAndSubmit :training="training" :isSaving.sync="isSaving" :isEditing.sync="isEditing" @onCancel="resetTraining" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
            </v-col>
            <v-col cols="auto">
              <CourseApproveOrReject :training="training" :isEditing="isEditing" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
            </v-col>
          </v-row>

          <div style="width: 100%; max-width: 450px;">
            <CourseImage :training.sync="training" :isEditing="isEditing" class="rounded-lg overflow-hidden" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
          </div>
        </v-col>
      </v-row>
      
      <!-- Horizontal Tabs -->
      <v-tabs v-model="selectedTab" background-color="transparent" color="primaryDark" class="mt-4">
        <v-tab v-for="tab in tabs" :key="tab" :href="'#' + tab" class="font-weight-bold" style="text-transform: none; letter-spacing: normal;">
          <span :class="tab === 'feedback' && hasInvalidFeedback ? 'error--text' : ''">
            {{ $t("course.courseDetails." + tab) }}
          </span>
        </v-tab>
      </v-tabs>

      <v-divider class="mb-6"></v-divider>

      <!-- Tab Content Area -->
      <v-container fluid class="pa-0">
        <v-tabs-items v-model="selectedTab" style="background: transparent;">
          <!-- ABOUT -->
          <v-tab-item value="about">
            <CourseAbout :training.sync="training" :isEditing="isEditing" :selectedLocale="selectedLocale" />
          </v-tab-item>

          <!-- INSTRUCTIONS -->
          <v-tab-item value="instructions">
            <CourseInstructions :training.sync="training" :isEditing="isEditing" :selectedLocale="selectedLocale" />
          </v-tab-item>

          <!-- FEEDBACK -->
          <v-tab-item value="feedback">
            <CourseFeedbackQuiz :training.sync="training" :isEditing="isEditing" :selectedLocale="selectedLocale" />
          </v-tab-item>

          <!-- SYLLABUS -->
          <v-tab-item value="syllabus">
            <v-row v-if="training.trainingModules && training.trainingModules.length > 0 || isEditing">
              <v-col cols="12" md="4" class="pr-md-6 border-right" style="border-right: 1px solid #eeeeee;">
                <CourseSyllabus :training.sync="training" :isEditing="isEditing" :selectedLocale="selectedLocale" class="mb-md-6" @onTrainingModuleContentSelection="onTrainingModuleContentSelection" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
              </v-col>
              <v-col cols="12" md="8" class="pl-md-6">
                <div id="courseModuleContent"></div>

                <template v-if="selectedTrainingModuleContent">
                  <CourseContentVideo v-if="canShowContent('VIDEO')" :key="courseContentRenderKey" :training.sync="training" :selectedTrainingModuleContent.sync="selectedTrainingModuleContent" :isEditing="isEditing" :selectedLocale="selectedLocale" class="mb-6" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
                  <CourseContentAudio v-else-if="canShowContent('AUDIO')" :key="courseContentRenderKey" :training.sync="training" :selectedTrainingModuleContent.sync="selectedTrainingModuleContent" :isEditing="isEditing" :selectedLocale="selectedLocale" class="mb-6" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
                  <CourseContentText v-else-if="canShowContent('TEXT')" :key="courseContentRenderKey" :training.sync="training" :selectedTrainingModuleContent.sync="selectedTrainingModuleContent" :isEditing="isEditing" :selectedLocale="selectedLocale" class="mb-6" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
                  <CourseContentQuiz v-else-if="canShowContent('QUIZ')" :key="courseContentRenderKey" :training.sync="training" :selectedTrainingModuleContent.sync="selectedTrainingModuleContent" :isEditing="isEditing" :selectedLocale="selectedLocale" class="mb-6" @onSuccess="snackbarSuccess = true" @onError="snackbarError = true" />
                </template>
                <template v-else>
                  <div class="text-center pa-10 text--secondary">
                    Select a module to view its content
                  </div>
                </template>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col class="text-center pa-10 text--secondary">
                No syllabus modules available. Click EDIT to add modules.
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-container>

    </v-container>

    <v-container v-else>
      <Error />
    </v-container>

    <v-snackbar v-model="snackbarSuccess" timeout="3000" color="success">
      {{ snackbarSuccessMessage || $t("course.courseDetails.successfullySave") }}
      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="snackbarSuccess = false">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="snackbarError" timeout="3000" color="error">
      {{ snackbarErrorMessage || $t("somethingWentWrong") }}
      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="snackbarError = false">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import CourseTitle from "../components/course-details/CourseTitle";
import CourseDuration from "@/app/course-module/components/course-details/CourseDuration";
import CourseValidity from "@/app/course-module/components/course-details/CourseValidity";
import CourseSyllabus from "../components/course-details/CourseSyllabus";
import CourseSaveAndSubmit from "@/app/course-module/components/course-details/CourseSaveAndSubmit";
import CourseApproveOrReject from "../components/course-details/CourseApproveOrReject";
import CourseImage from "../components/course-details/CourseImage";
import CourseAbout from "../components/course-details/CourseAbout";
import CourseInstructions from "../components/course-details/CourseInstructions";
import CourseContentVideo from "../components/course-details/CourseContentVideo";
import CourseContentAudio from "../components/course-details/CourseContentAudio";
import CourseContentText from "../components/course-details/CourseContentText";
import CourseContentQuiz from "../components/course-details/CourseContentQuiz";
import Error from "../../core-module/components/Error";
import CoursePrice from "@/app/course-module/components/course-details/CoursePrice";
import CourseCategory from "@/app/course-module/components/course-details/CourseCategory";
import CourseFeedbackQuiz from "../components/course-details/CourseFeedbackQuiz.vue";

export default {
  name: "CourseDetailsPage",
  components: {
    CourseCategory,
    CoursePrice,
    CourseTitle,
    CourseDuration,
    CourseValidity,
    CourseSyllabus,
    CourseSaveAndSubmit,
    CourseApproveOrReject,
    CourseImage,
    CourseAbout,
    CourseInstructions,
    CourseContentVideo,
    CourseContentAudio,
    CourseContentText,
    CourseContentQuiz,
    CourseFeedbackQuiz,
    Error,
  },
  data() {
    return {
      snackbarSuccess: false,
      snackbarError: false,
      snackbarSuccessMessage: null,
      snackbarErrorMessage: null,
      loading: true,
      trainingOg: null,
      training: null,
      isSaving: false,
      isEditing: false,
      selectedLocale: "en",
      tabs: ["about", "instructions", "feedback", "syllabus"],
      selectedTab: "",
      selectedTrainingModuleContent: null,
    };
  },
  computed: {
    hasInvalidFeedback() {
      return this.training.feedback?.questionAnswers?.some(
        fq => fq.hasInvalidQuestion || fq.hasInvalidChoices
      );
    },
    courseContentRenderKey() {
      const trainingModuleIndex =
        this.selectedTrainingModuleContent?.trainingModuleIndex;
      const trainingModuleContentIndex =
        this.selectedTrainingModuleContent?.trainingModuleContentIndex;
      const contentType =
        this.training.trainingModules[trainingModuleIndex]
          ?.trainingModuleContents[trainingModuleContentIndex]?.contentType;

      return `${contentType}_${trainingModuleIndex}_${trainingModuleContentIndex}`;
    },
  },
  watch: {
    "$route.params.trainingId": {
      handler() {
        this.fetchTraining();
      },
      immediate: true,
    },
  },
  methods: {
    fetchTraining() {
      this.loading = true;
      this.trainingOg = null;
      this.training = null;
      this.selectedTab = "";
      this.selectedTrainingModuleContent = null;

      if (this.$route.params.trainingId) {
        this.$apollo.addSmartQuery("getTraining", {
          query: require("../api/query/getTraining.gql"),
          variables: { id: this.$route.params.trainingId },
          result({ data, loading }) {
            if (data && data.getTraining) {
              this.trainingOg = this.sortTrainingModulesInTraining(
                data.getTraining
              );
              //TODO: find a way to prevent pending changes not reset on individual save
              this.training = this.deepCloneObject(this.trainingOg);
              this.training = this.validateTraining(this.training);

              if (this.selectedTab === "") this.selectedTab = "about";
            }

            this.loading = loading;
          },
        });
      } else {
        this.trainingOg = {
          // trainingCategories: [],
          // title: [],
          // description: [],
          // instructions: [],
          // images: [],
          // certificateValidity: null,
          isActive: true,
          hasInvalidTitle: true,
          // trainingModules: [],
        };

        this.training = this.deepCloneObject(this.trainingOg);

        this.isEditing = true;
        this.loading = false;
      }
    },
    resetTraining() {
      this.training = this.deepCloneObject(this.trainingOg);
      this.training = this.validateTraining(this.training);
      this.selectedTrainingModuleContent = null;
    },
    canShowContent(contentType) {
      return (
        this.training.trainingModules[
          this.selectedTrainingModuleContent?.trainingModuleIndex
        ]?.trainingModuleContents[
          this.selectedTrainingModuleContent?.trainingModuleContentIndex
        ]?.contentType === contentType
      );
    },
    onTrainingModuleContentSelection(trainingModuleContent) {
      this.selectedTab = "syllabus";
      this.selectedTrainingModuleContent = trainingModuleContent;

      if (
        this.selectedTrainingModuleContent &&
        this.$vuetify.breakpoint.mdAndDown
      ) {
        setTimeout(() => {
          const el = document.getElementById("courseModuleContent");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    },
  },
};
</script>

<style lang="scss" scoped />
